import { useSyncExternalStore } from 'react'

/*
 * A minimal observable value, replacing the mobx `makeAutoObservable` slices.
 *
 * The point of keeping a module-level store rather than lifting everything
 * into context: notifications, the breadcrumb path and the websocket are
 * imperative APIs called from event handlers, effects and axios callbacks --
 * places where a plain function call is what you want, not a hook. Components
 * subscribe with `useStore`, which is `useSyncExternalStore`, so React 19's
 * concurrent rendering stays correct.
 */
export interface Store<T> {
  get(): T
  set(next: T | ((prev: T) => T)): void
  subscribe(listener: () => void): () => void
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial
  const listeners = new Set<() => void>()

  return {
    get: () => value,
    set(next) {
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(value) : next
      if (Object.is(resolved, value)) return
      value = resolved
      listeners.forEach(listener => listener())
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    }
  }
}

export function useStore<T>(store: Store<T>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}
